// AccountAvatar.tsx — the user's avatar in the header (AvatarImage) and the Account section of the settings
// panel (AccountAvatarSection) where a signed-in user adds, changes or removes it.
// Three states: the default gray silhouette (no picture), a link to an image on any web site, or an upload.
// Sign-up and this section render the same ProfilePicturePicker and store the picture through the same helper in
// utils/botFactoryAPI (resolveProfilePic), so both screens offer the same options and work the same way.
// A picture that fails to load (a 404) shows the silhouette.
import React, { useEffect, useState } from 'react';
import { ProfilePicturePicker } from '../../utils/upload/ProfilePicturePicker';
import { updateUserAvatar } from '../../utils/botFactoryAPI';

type AvatarImageProps = {
  src?: string;
  className?: string;
  id?: string;
  alt?: string;
  iconSize?: number;
  /** A round picture of this size (the silhouette is the same size). Used by Account and sign-up. */
  size?: number;
  style?: React.CSSProperties;
};

// The picture, or the standard gray silhouette when there is none or it fails to load (a 404).
export function AvatarImage({ src, className, id, alt = 'Avatar', iconSize = 24, size, style }: AvatarImageProps) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) {
    return <i id={id} className="fas fa-user-circle" role="img" aria-label={alt} style={{ fontSize: size ?? iconSize, color: 'gray', flex: 'none' }} />;
  }
  const round: React.CSSProperties | undefined = size
    ? { width: size, height: size, borderRadius: '50%', objectFit: 'cover', flex: 'none' }
    : undefined;
  return <img id={id} className={className} style={{ ...round, ...style }} alt={alt} src={src} onError={() => setFailed(true)} />;
}

type AccountAvatarSectionProps = {
  userId: string;
  token: string;
  /** The picture to show now: a resolved image URL, or undefined for the silhouette. */
  currentUrl?: string;
  /** Called with the stored `pic` value after a successful change, so the caller can update the header. */
  onChanged: (pic: string) => void;
};

export function AccountAvatarSection({ userId, token, currentUrl, onChanged }: AccountAvatarSectionProps) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);

  const save = async (pic: File | string | null) => {
    setBusy(true);
    setMessage(null);
    try {
      const stored = await updateUserAvatar(userId, token, pic);
      onChanged(stored);
      setMessage({ text: pic === null ? 'Picture removed.' : 'Picture saved.', error: false });
    } catch (err: any) {
      setMessage({ text: err?.message || 'The picture could not be saved.', error: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="settings-panel-group account-avatar" data-name="account-avatar">
      <ProfilePicturePicker
        onChange={save}
        disabled={busy}
        hasPicture={!!currentUrl}
        showFilePreview={false}
        leading={<AvatarImage src={currentUrl} alt="Your picture" size={56} />}
      />
      {message && (
        <div className={`account-avatar-message${message.error ? ' account-avatar-message--error' : ''}`} role={message.error ? 'alert' : 'status'}>
          {message.text}
        </div>
      )}
    </div>
  );
}
